import Map from './Map.vue';
import MapLoader from './MapLoader.vue';
import MapProvider from './MapProvider.vue';
import DrawVisualizations from './DrawVisualizations.vue';
import StatsTravel from './StatsTravel.vue';
import moment from 'moment';

import { dateTypes, IDescription, Marker, Point, PointsOfInterest } from '../../models';
import {
    getDateByFormat,
    getDateFromDateFormatApi,
    getTimeAgo,
    hasElements,
    MarkerVehicleHistoricalUUID,
    getStringTimeFromDate,
    getPositionsFiltered,
    parseFloatNumber,
    isNumber
} from '../../utils';
import { min } from 'moment';
import { getValueParsedByType, unitsTypes } from '../../../core/api';
import { getInfoWindow } from '../../../tracking/map-markers-infos';
export { MapProvider, MapLoader, Map, DrawVisualizations, StatsTravel };

export const latinamericaBounds = {
    north: 32.915085,
    south: -58.660268,
    west: -120.388484,
    east: -6.799613
};

export const initMapCenter: any = {
    lat: -32.818798,
    lng: -55.948865
};

export const getBoundsFromMarkers = (markers: Marker[]) => {
    let bound = new google.maps.LatLngBounds();
    markers.forEach(m => {
        bound.extend({ lat: m.lat, lng: m.lng });
    });
    return bound;
};

const getFakePointsOffsetStatsPanel = (lng: number, maxLat: number, minLat: number, mapClientHeight: number) => {
    //panel is 480 pixels, fixed
    //offset margin is for google maps top and bottom margin
    let panelSize = 480;
    let offsetMargin = 1.2;
    let mapSize = mapClientHeight - panelSize;
    let panelHeightRelation = panelSize / mapClientHeight;
    let mapHeightRelation = mapSize / mapClientHeight;
    //diffLats es a mapsizerelation
    //como x es a panelsizerelation
    let diffLats: number = maxLat - minLat;
    let diffLatsOffset = offsetMargin * ((diffLats * panelHeightRelation) / mapHeightRelation);
    let newLat: number = minLat - diffLatsOffset;
    return { lat: newLat, lng: lng };
};

export const getMiddle = (start, end) => Math.floor((start + end) / 2);

export const optimizedFilterPoints = (points, min_lat, max_lat, min_lng, max_lng) => {
    // Filtro por latitud usando busqueda binaria
    let startIndex = 0;
    let endIndex = points.length - 1;
    let minIndex: any = null;
    let maxIndex: any = null;
    let counter = 0;
    // Busco el primer elemento del array de puntos que esta dentro
    // De los bounds del mapa
    while (startIndex < endIndex) {
        let middleIndex = getMiddle(startIndex, endIndex);
        let currentPoint = points[middleIndex];
        counter += 1;
        // Si el punto esta incluido
        if (currentPoint.lat >= min_lat && currentPoint.lat <= max_lat) {
            // Si encontre el extremo detengo el loop
            if (middleIndex == 0 || points[middleIndex - 1].lat < min_lat) {
                minIndex = middleIndex;
                break;
            }
            // Si el punto anterior sigue dentro del rango dejo el punto actual como maximo
            else {
                endIndex = middleIndex;
            }
        }
        // Si esta por encima del rango
        else if (currentPoint.lat > max_lat) {
            endIndex = middleIndex - 1;
        }
        // Si esta por debajo del rango
        else if (currentPoint.lat < min_lat) {
            startIndex = middleIndex + 1;
        }
    }
    // console.log("============================");
    // console.log("minIndex: ", minIndex);
    // console.log("============================");
    // Si no encontre ningun punto dentro del rango es que no hay ninguno
    if (minIndex == null) {
        return [];
    }
    // Si solo hay un elemento
    if (startIndex == endIndex) {
        return [points[startIndex]];
    }
    // Busco el ultimo elemento del array de puntos que esta dentro
    // De los bounds del mapa
    startIndex = minIndex;
    endIndex = points.length - 1;
    while (startIndex <= endIndex) {
        counter += 1;
        let middleIndex = getMiddle(startIndex, endIndex);
        let currentPoint = points[middleIndex];
        // Si el punto esta incluido
        if (currentPoint.lat >= min_lat && currentPoint.lat <= max_lat) {
            // Si encontre el extremo detengo el loop
            if (middleIndex == points.length - 1 || points[middleIndex + 1].lat > max_lat) {
                maxIndex = middleIndex;
                break;
            }
            // Si el punto siguiente sigue dentro del rango dejo el punto actual como minimo
            else {
                startIndex = middleIndex + 1;
            }
        }
        // No puede estar por debajo del rango porque el indice de inicio es el minimo incluido,
        // por lo que asumimos que si no esta en el rango, es que esta por encima
        else {
            endIndex = middleIndex - 1;
        }
    }
    // console.log("============================");
    // console.log("maxIndex: ", maxIndex);
    // console.log("============================");
    // Filtro por longitud
    let filtered_elements: any = [];
    for (let i = minIndex; i <= maxIndex; i++) {
        counter += 1;
        if (points[i].lng >= min_lng && points[i].lng <= max_lng) {
            filtered_elements.push(points[i]);
        }
    }
    // console.log("Ciclos totales: ", counter);
    return filtered_elements;
};

export const getRandom = (arr, n) => {
    let result = new Array(n),
        len = arr.length,
        taken = new Array(len);
    if (n > len) throw new RangeError('getRandom: more elements taken than available');
    while (n--) {
        let x = Math.floor(Math.random() * len);
        result[n] = arr[x in taken ? taken[x] : x];
        taken[x] = --len in taken ? taken[len] : len;
    }
    return result;
};

//if panel is open add 2 points in the bounds to account for stats panel
export const getBoundsFromPosition = (positions: Point[], statsOpen: boolean, mapClientHeight: number) => {
    let bound = new google.maps.LatLngBounds();
    let lng: number = 0;
    let minLat: number = +Infinity;
    let maxLat: number = -Infinity;
    
    positions = positions.filter(m => m.lat !== 0 && m.lng !== 0);

    positions.forEach(m => {
        if (statsOpen) {
            if (!lng) {
                lng = m.lng;
            }
            if (m.lat < minLat) {
                minLat = m.lat;
            }
            if (m.lat > maxLat) {
                maxLat = m.lat;
            }
        }
        bound.extend({ lat: m.lat, lng: m.lng });
    });
    if (statsOpen) {
        bound.extend(getFakePointsOffsetStatsPanel(lng, maxLat, minLat, mapClientHeight));
    }
    return bound;
};

export const getBoundsFromMVCArray = (positions: google.maps.MVCArray<google.maps.LatLng>) => {
    let bound = new google.maps.LatLngBounds();
    positions.forEach(m => {
        bound.extend({ lat: m.lat(), lng: m.lng() });
    });
    return bound;
};

export const getEpsilon = (zoom: any) => {
    let k50 = 50000;
    let k30 = 30000;
    let k20 = 20000;
    let eps = [k50, k50, k50, k30, k30, k30, k20, 5000, 1200, 800, 700, 400, 120, 80, 20, 18, 14, 12, 10, 9, 6];
    if (zoom < eps.length) {
        let epsilon = eps[zoom];
        return epsilon;
    } else return 2;
};

export const GDouglasPeucker = (source: Marker[], kink, bounds: google.maps.LatLngBounds): Marker[] => {
    let n_source, n_stack, n_dest, start, end, i, sig;
    let dev_sqr, max_dev_sqr, band_sqr;
    let x12, y12, d12, x13, y13, d13, x23, y23, d23;
    let F = (Math.PI / 180.0) * 0.5;
    let index = new Array();
    let sig_start = new Array();
    let sig_end = new Array();

    if (source.length < 3) return source;
    n_source = source.length;
    band_sqr = (kink * 360.0) / (2.0 * Math.PI * 6378137.0);
    band_sqr *= band_sqr;
    n_dest = 0;
    sig_start[0] = 0;
    sig_end[0] = n_source - 1;
    n_stack = 1;
    while (n_stack > 0) {
        start = sig_start[n_stack - 1];
        end = sig_end[n_stack - 1];
        n_stack--;

        if (end - start > 1) {
            x12 = source[end].lng - source[start].lng;
            y12 = source[end].lat - source[start].lat;
            if (Math.abs(x12) > 180.0) x12 = 360.0 - Math.abs(x12);
            x12 *= Math.cos(F * (source[end].lat + source[start].lat)); /* use avg lat to reduce lng */
            d12 = x12 * x12 + y12 * y12;

            for (i = start + 1, sig = start, max_dev_sqr = -1.0; i < end; i++) {
                x13 = source[i].lng - source[start].lng;
                y13 = source[i].lat - source[start].lat;
                if (Math.abs(x13) > 180.0) x13 = 360.0 - Math.abs(x13);
                x13 *= Math.cos(F * (source[i].lat + source[start].lat));
                d13 = x13 * x13 + y13 * y13;

                x23 = source[i].lng - source[end].lng;
                y23 = source[i].lat - source[end].lat;
                if (Math.abs(x23) > 180.0) x23 = 360.0 - Math.abs(x23);
                x23 *= Math.cos(F * (source[i].lat + source[end].lat));
                d23 = x23 * x23 + y23 * y23;

                if (d13 >= d12 + d23) dev_sqr = d23;
                else if (d23 >= d12 + d13) dev_sqr = d13;
                else dev_sqr = ((x13 * y12 - y13 * x12) * (x13 * y12 - y13 * x12)) / d12; // solve triangle

                if (dev_sqr > max_dev_sqr) {
                    sig = i;
                    max_dev_sqr = dev_sqr;
                }
            }

            if (max_dev_sqr < band_sqr) {
                index[n_dest] = start;
                n_dest++;
            } else {
                n_stack++;
                sig_start[n_stack - 1] = sig;
                sig_end[n_stack - 1] = end;
                n_stack++;
                sig_start[n_stack - 1] = start;
                sig_end[n_stack - 1] = sig;
            }
        } else {
            index[n_dest] = start;
            n_dest++;
        }
    }

    index[n_dest] = n_source - 1;
    n_dest++;

    let r: Marker[] = [];
    for (let i = 0, j = n_dest; i < j; i++) {
        if (bounds.contains(getLatLng(source[index[i]]))) {
            r.push(source[index[i]]);
        }
    }
    /* for (let i = 0, j = source.length; i < j; i++) {
        if (source[i].marker.description === 'cero') {
            r.push(source[i]);
        }
    }*/
    return r;
};

export const getContentInfoWindows = (
    marker: Marker,
    isSmartwayUser: boolean,
): string => {
    if (marker.info) {
        return (
            '<div id="content">' +
            `<span class='black-description'>${marker.info.vehicle_plate}</span> &mdash;` +
            `<span>${marker.info.description}&mdash;</span> ` +
            `${isSmartwayUser ? `<span>VIN: ${marker.info.vin}&mdash;</span> ` : ''}` +
            ` <span> ${
                marker.infoState && marker.infoState.speed ? Math.round(marker.infoState.speed) + ' Km/h &mdash;' : ''
            }</span>` +
            `
                 <span><i> ${
                     marker.infoState && marker.infoState.timestamp
                         ? getTimeAgo(getDateFromDateFormatApi(marker.infoState.timestamp))
                         : ''
                 }</i></span>
                ` +
            `</div>`
        );
    } else {
        return '';
    }
};

export const getContentInfoVehicleInsurance = markerValue => {
    let content = '';

    if (markerValue) {
        content = '<div id="content">';

        if (markerValue.vehicle_plate) {
            content += `<span style="font-weight: bold; font-size: 14px">${markerValue.vehicle_plate}</span><br>`;
        }
        if (markerValue.description) {
            content += `<span>${markerValue.description}</span><br> `;
        }
        if (markerValue.vin) {
            content += `<span>VIN: ${markerValue.vin}</span><br> `;
        }

        if (markerValue.type) {
            content += `<b><span> ${markerValue.type === 'sowing' ? 'Sembradora' : 'Cosechadora'}</span></b><br> `;
        }
        if (markerValue.position && markerValue.position.timestamp) {
            content += `<span> ${
                markerValue && markerValue.position && markerValue.position.timestamp
                    ? getDateByFormat(
                          dateTypes.dateFormatFromDisplayInfo,
                          getDateFromDateFormatApi(markerValue.position.timestamp)
                      )
                    : ''
            }</span> `;
        }

        content += `</div>`;
    }

    return content;
};

export const getContentInfoWindowsHistorical = (
    marker: Marker,
    isSmartwayUser: boolean,
): string => {
    let tooltip = ''

    if (marker.info) {
        tooltip = `<div id="content">` +
            `<span class='black-description'>${marker.info.vehicle_plate}</span>` +
            `<br>` +
            `<span>${marker.info.description}</span> ` +
            `${isSmartwayUser ? `<span>VIN: ${marker.info.vin}</span> ` : ''}` +
            `<br>` +
            ` <span> ${marker.infoState && marker.infoState.speed ? Math.round(marker.infoState.speed) + ' Km/h' : ''}</span> ` +
            `<br>` +
            `
                 <span> ${
                     marker.infoState && marker.infoState.timestamp
                         ? getDateByFormat(
                                dateTypes.dateFormatFromDisplayInfo,
                               getDateFromDateFormatApi(marker.infoState.timestamp)
                           )
                         : ''
                 }</span>
                ` +
            `</div>`
    }

    return tooltip;
};

export const getContentInfoWindowsStop = (start: Date, end: Date): string => {
    // console.log("getContentInfoWindowsStop", start, end);
    let dateString =
        start.getDate() + '/' + (start.getMonth() + 1) + '/' + start.getFullYear() + ' ' + getStringTimeFromDate(start);
    let time = end.getTime() - start.getTime();
    let hours = Math.floor(time / (1000 * 60 * 60));
    time -= hours * 1000 * 60 * 60;
    let minutes = Math.floor(time / (1000 * 60));
    let timeString =
        (hours > 0 ? hours + (hours > 1 ? ' horas y ' : ' hora y ') : '').toString() + minutes + ' minutos';
    return (
        '<div id="content">' +
        `<span class='fs-12'>Parada: <b>${dateString}</b> durante <b>${timeString}</b></span>` +
        `</div>`
    );
};

export const getContentInfoWindowsForFailure = (
    timestamp: string, 
    dtcDescription: string,
    vehicleDescription:string
): string => {
    return (
        '<div id="content">' +
        `<span class='fs-12'>Falla: <b>${dtcDescription}</b> </span><br>` +
        `<span class='fs-12'>Fecha: <b>${timestamp}</b> </span><br>` +
        `<span class='fs-12'>Vehículo: <b>${vehicleDescription}</b> </span>` +
        `</div>`
    );
};

export const getContentInfoForLastReport = (
    marker: Marker,
    isMDPUser: boolean = false
): string => {
    let tooltip = '';
    if (marker.info) {
        tooltip = '<div id="content">' + `<span class='marker-description'>${marker.info.description}</span>`;

        if (marker.infoState && marker.infoState.speed) {
            tooltip += `<span>— ${Math.round(marker.infoState.speed)} Km/h</span>`;
        }

        if(marker && marker.infoState && marker.infoState.work_order_number && isMDPUser){
            tooltip += `<br><span>N° OT: ${marker.infoState.work_order_number}</span>`;
        }
    }

    tooltip += `</div>`;

    return tooltip;
}

export const getContentInfoWindowsVisualization = (marker: Marker): string => {
    if (marker.info) {
        const speed = marker && marker.infoState && marker.infoState.speed !== undefined? `${parseSpeedValue(marker.infoState.speed)} Km/h` : '';
        return (
            '<div id="content">' +
                `<span class='marker-description'>${marker.info.description}</span>` +
                `<span>— ${speed}</span>` +
            `</div>`
        );
    } else {
        return '';
    }
};

export const getContentInfoForFilters = (marker: Marker): string => {
    // console.log("getContentInfoForFilters", {
    //     marker: marker
    // })
    if (marker.info && marker.infoState && marker.infoState) {
        let value = marker.infoState.value;
        let varKey:any = marker.infoState.variable_key;
        if(typeof value === 'number' && varKey){
            value = getValueParsedByType(value, varKey);
        }
        let tooltip = '<div id="content">' + `<span class='marker-description'> ${marker.info.description} </span><br>`;

        if(marker.infoState && marker.infoState.variable_description){
            tooltip += `<span>
                            ${marker.infoState.variable_description} — 
                            ${value} ${marker.infoState.variable_unit} 
                        </span>`
        }
        

        tooltip += `</div>`;
        return tooltip;
    } else {
        return '';
    }
}

export const parseSpeedValue = (value: null | number):string | number =>{
    if(value !== null && isNumber(value)){
       return Math.round(value)
    }
    return '-'
}

const getLatLng = (marker: Marker): google.maps.LatLng => new google.maps.LatLng(marker.lat, marker.lng);

export const isMarkerArray = (array: Marker[] | MarkerVehicleHistoricalUUID[]): boolean => !array[0]['id'];

export const extractMarkersPositions = (markers: Marker[]): Point[] => markers.map(m => ({ lat: m.lat, lng: m.lng }));

export const isMarkerVehicleHistoricalArray = (array: Marker[] | MarkerVehicleHistoricalUUID[]): boolean =>
    !!array[0]['id'];

export const extractAllMarkersArray = (markers: MarkerVehicleHistoricalUUID[]): Marker[] =>
    hasElements(markers) ? markers.reduce((result, it) => [...result, ...it['markers']], []) : [];

export const chooseMarkerPositions = (updates: Marker[] | MarkerVehicleHistoricalUUID[]): Point[] => {
    if (hasElements(updates) && isMarkerArray(updates)) {
        return extractMarkersPositions(updates as Marker[]);
    } else if (hasElements(updates) && isMarkerVehicleHistoricalArray(updates)) {
        return extractMarkersPositions(extractAllMarkersArray(updates as MarkerVehicleHistoricalUUID[]) as Marker[]);
    } else {
        return [];
    }
};

const getLineSymbol = (color: string) => ({
    strokeColor: color,
    path: 'M 0,-1 0,1',
    strokeOpacity: 0.4,
    scale: 2
});

export const getUnselectedLines = (
    markers: Marker[],
    start: number,
    end: number,
    color: string
): google.maps.Polyline[] => {
    markers = getPositionsFiltered(markers);
    const firstUnSelectedPath: google.maps.Polyline = new google.maps.Polyline({
        path: chooseMarkerPositions(markers.slice(0, start)),
        geodesic: true,
        strokeOpacity: 0,
        icons: [
            {
                icon: getLineSymbol(color),
                offset: '0',
                repeat: '20px'
            }
        ]
    });
    const secondUnselectedPath = new google.maps.Polyline({
        path: chooseMarkerPositions(markers.slice(end + 1, markers.length - 1)),
        geodesic: true,
        strokeOpacity: 0,
        icons: [
            {
                icon: getLineSymbol(color),
                offset: '0',
                repeat: '20px'
            }
        ]
    });
    return [firstUnSelectedPath, secondUnselectedPath];
};


export const createInfo = (marker: Marker, position?: google.maps.LatLng): google.maps.InfoWindow => {
    return position
        ? getInfoWindow(getContentInfoWindowsVisualization(marker), position)
        : getInfoWindow(getContentInfoWindowsVisualization(marker))
};

export const rightClickEvent = 'rightclick';
export const mouseoverEvent = 'mouseover';
export const mouseoutEvent = 'mouseout';
export const mouseClickEvent = 'click';
export const mouseEnterEvent = 'mouseenter';
export const mouseLeaveEvent = 'mouseleave';
export const onContextMenu = 'context-menu';
export const onMouseDown = 'mousedown';

